// Shared UI primitives used across multiple feature components.
// Originally lived inline in deskte-full.jsx; extracted here so feature
// modules under crm-app/components/* can compose them without circular
// imports.
import { useRef, useReducer, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Bold, Highlighter, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import EmojiPicker from './EmojiPicker.jsx';
import DateField from './DateField.jsx';
import { filtrarPorTexto, clientSearchHaystack } from '../searchNorm.js';
import { noteMarkersToHtml, noteHtmlToMarkers, parseNoteTree, isRichNote, noteToPlainText } from '../noteFormat.js';
import { useMencionesEnEditable } from './MenuDeMenciones.jsx';

// Client picker — a real searchable dropdown (drop-in for the old plain <select>).
// Type to filter by name/email and the matches appear in a dropdown right away
// (the old version typed into a box that filtered a SEPARATE collapsed <select>,
// so nothing seemed to happen — Lissette 2026-06-18). The menu renders in a body
// portal so it's never clipped in a modal. `onChange` still receives a native-
// like `{ target: { value: id } }` so every call site keeps its handler.
// `optionLabel(c)` customizes row text; `noneLabel={false}` hides the "none" row.
//
// No solo sirve para clientes: `haystack(item)` dice por que texto se busca y
// `placeholder` cambia el rotulo, asi que cualquier lista de {id, etiqueta}
// puede usar este mismo desplegable con buscador en vez de un <select> pelado
// (Pagos del Mes, al asignar la poliza tras subir el archivo — Lissette
// 2026-09-02, punto 65). Antes de copiar este componente, pasale un haystack.
export function ClientSearchSelect({ clients, value, onChange, lang, label, noneLabel, optionLabel, haystack, placeholder, selectStyle, selectClassName }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null); // null = show selected name; string = typing
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const fmtOpt = optionLabel || ((c) => `${c.firstName || ''} ${c.lastName || ''}`.trim());
  const selected = (clients || []).find((c) => c.id === value) || null;
  const text = query == null ? (selected ? fmtOpt(selected) : '') : query;
  // Same tokenized, accent-insensitive matching as the Clients list. This used
  // to `includes()` the whole query against "first last email", so "julio
  // perez" missed "Julio Pérez" / "Julio Cesar Perez" / "Perez, Julio", and it
  // ignored middleName, spouse and dependents entirely (Lissette 2026-07-21).
  const textoDe = haystack || clientSearchHaystack;
  const list = filtrarPorTexto(clients || [], query == null ? '' : query, textoDe, 300);
  const place = () => { const el = inputRef.current; if (el) setRect(el.getBoundingClientRect()); };
  useEffect(() => {
    if (!open) return;
    place();
    const reposition = () => place();
    const onDown = (e) => {
      const t = e.target;
      const inWrap = wrapRef.current && wrapRef.current.contains(t);
      const inPop = t.closest && t.closest('[data-clientsel-pop]');
      if (!inWrap && !inPop) { setOpen(false); setQuery(null); }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open]);
  const pick = (id) => { onChange && onChange({ target: { value: id } }); setQuery(null); setOpen(false); };
  const pop = open && rect
    ? createPortal(
        <div data-clientsel-pop style={{ position: 'fixed', zIndex: 100000, left: rect.left, top: rect.bottom + 2, width: rect.width, maxHeight: 280, overflowY: 'auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,.5)' }}>
          {noneLabel !== false && (
            <div onMouseDown={(e) => { e.preventDefault(); pick(''); }} style={{ padding: '.45rem .6rem', fontSize: '.8rem', cursor: 'pointer', color: 'var(--c-text3,#45688a)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--c-surface3,#162d44)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>{noneLabel || (es ? '— Sin cliente —' : '— No client —')}</div>
          )}
          {list.length === 0 && <div style={{ padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Sin resultados' : 'No matches'}</div>}
          {list.map((c) => (
            <div key={c.id} onMouseDown={(e) => { e.preventDefault(); pick(c.id); }} style={{ padding: '.45rem .6rem', fontSize: '.8rem', cursor: 'pointer', color: 'var(--c-text,#c8dff0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', background: c.id === value ? 'var(--c-surface3,#162d44)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--c-surface3,#162d44)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = c.id === value ? 'var(--c-surface3,#162d44)' : 'transparent')}>
              {fmtOpt(c)}{c.email ? ` · ${c.email}` : ''}
            </div>
          ))}
        </div>,
        document.body,
      )
    : null;
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {label && <label className="dkt-input-lbl">{label}</label>}
      <input
        ref={inputRef}
        className={selectClassName || 'dkt-inp'}
        value={text}
        placeholder={placeholder || (es ? '🔍 Buscar cliente por nombre…' : '🔍 Search client by name…')}
        autoComplete="off"
        onFocus={() => { setOpen(true); setQuery(''); }}
        onClick={() => { setOpen(true); place(); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQuery(null); } }}
        style={{ width: '100%', boxSizing: 'border-box', ...(selectStyle || {}) }}
      />
      {pop}
    </div>
  );
}

// In-app full-screen image viewer. A base64 `data:` URL cannot be opened in a
// new browser tab (browsers/CSP block top-level navigation to data: URIs), so
// pasted note photos would silently fail to open via <a target=_blank>.
// Rendering the same data URL inside an <img> overlay works. z-index sits above
// modals (PHI dialogs use 100000).
export function ImageLightbox({ src, name, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100001, background: 'rgba(0,0,0,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', cursor: 'zoom-out' }}>
      <img src={src} alt={name || ''} style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', borderRadius: 8 }} />
      <button type="button" onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(0,0,0,.55)', border: '1px solid rgba(255,255,255,.3)', color: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><X size={18} /></button>
    </div>
  );
}

export function Modal({ title, onClose, children, wide, xl, ancho, zIndex }) {
  // `ancho`: un ancho máximo propio para la ventana que no cabe en 800 (el
  // cotizador del Mercado, E-409). En pantallas chicas sigue ocupando el 100%.
  const mw = ancho || (xl ? 800 : wide ? 640 : 440);
  // Clicking the backdrop does NOT close the modal. Agents kept losing
  // in-progress work by accidentally clicking just outside a form ("sin
  // querer le da click afuera se cierra la ventana y toca volver a empezar").
  // The window now only closes via the X button or the form's own Cancel —
  // so an errant outside click leaves everything exactly where it was.
  // (Data loss is unacceptable — see feedback_data_persistence_is_sacred.)
  // Portal to <body> so stacked modals never nest in the DOM. When a
  // modal opened another (e.g. the SSN PIN modal over the client edit
  // form), the inner overlay used to be a DOM descendant of the outer
  // one; the backdrop close listener (mouseup) bubbled across the nested
  // overlays and a close/back click could land on the wrong element,
  // intermittently re-triggering the inner modal. A portal makes every
  // overlay a direct child of <body> — independent, correctly stacked.
  // `.dkt-overlay` pins every modal at z-index 50. Some screens render their
  // own custom overlay at a HIGHER z-index (e.g. the client-portal modal at
  // 100, PHI/HIPAA dialogs at 100000). When confirmDialog() is invoked from
  // on top of one of those, the default-50 confirm would fall BEHIND it
  // ("el anuncio de ¿está seguro? se va al fondo"). Callers that must sit on
  // top of everything pass an explicit zIndex to override the class.
  const node = (
    <div className="dkt-overlay" style={zIndex != null ? { zIndex } : undefined}>
      <div className="dkt-modal" style={{ maxWidth: mw }}>
        <div className="dkt-mhdr">
          <span className="dkt-mtitle">{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text2,#7aa3c0)', padding: 4, borderRadius: 6, transition: 'color .2s' }}><X size={17} /></button>
        </div>
        <div className="dkt-mbody">{children}</div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}

// ── Global branded confirm dialog ──────────────────────────────────────
// Drop-in replacement for the browser's native window.confirm, but rendered
// as an in-app modal that matches the Deskte theme and speaks the user's
// language. Usage from anywhere:
//   if (!(await confirmDialog('¿Eliminar este lead?'))) return;
// or with options:
//   await confirmDialog({ message, title, confirmLabel, danger: true });
// Mount <ConfirmHost lang={lang} /> exactly once at the app root.
let _confirmResolve = null;
let _confirmState = null;
let _confirmLang = 'es';
let _confirmTexto = '';
const _confirmListeners = new Set();
const _confirmEmit = () => { _confirmListeners.forEach((fn) => fn()); };

export function confirmDialog(opts) {
  const o = typeof opts === 'string' ? { message: opts } : (opts || {});
  return new Promise((resolve) => {
    // If a previous dialog is somehow still open, treat it as cancelled
    // so its awaiting caller doesn't hang forever.
    if (_confirmResolve) { const prev = _confirmResolve; _confirmResolve = null; prev(false); }
    _confirmResolve = resolve;
    _confirmState = o;
    _confirmEmit();
  });
}

/**
 * LO MISMO, PERO PIDIENDO UN TEXTO (Lissette 2026-10-26: "cuando le doy Ver
 * como no hace nada"). Sustituye a `window.prompt`, que el navegador puede
 * BLOQUEAR sin avisar —basta con haber marcado una vez "No permitir que esta
 * página cree más diálogos"—: entonces devuelve null al instante, como si se
 * hubiera cancelado, y el botón parece muerto. Esta ventana es de la app y no
 * se puede bloquear.
 *
 *   const motivo = await pedirTextoDialog({ title, message, placeholder, obligatorio: true });
 *   if (motivo === null) return; // canceló
 *
 * Devuelve el texto ya recortado, o null si se cancela.
 */
export function pedirTextoDialog(opts) {
  const o = opts || {};
  return new Promise((resolve) => {
    if (_confirmResolve) { const prev = _confirmResolve; _confirmResolve = null; prev(false); }
    _confirmResolve = resolve;
    _confirmTexto = String(o.valorInicial || '');
    _confirmState = { ...o, conTexto: true };
    _confirmEmit();
  });
}

export function ConfirmHost({ lang }) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const fn = () => force();
    _confirmListeners.add(fn);
    return () => { _confirmListeners.delete(fn); };
  }, []);
  // Keep the module-level language in sync so confirmDialog() callers don't
  // have to pass it on every call — the message text is already localized.
  _confirmLang = lang === 'en' ? 'en' : 'es';
  if (!_confirmState) return null;
  const o = _confirmState;
  const es = _confirmLang !== 'en';
  const danger = o.danger === true; // default: neutral confirm button
  const conTexto = o.conTexto === true;
  const textoListo = _confirmTexto.trim();
  const faltaTexto = conTexto && o.obligatorio === true && !textoListo;
  const done = (val) => {
    const r = _confirmResolve;
    _confirmResolve = null;
    _confirmState = null;
    _confirmEmit();
    if (r) r(val);
  };
  const confirmLabel = o.confirmLabel || (es ? 'Confirmar' : 'Confirm');
  const cancelLabel = o.cancelLabel || (es ? 'Cancelar' : 'Cancel');
  const title = o.title || (es ? '¿Estás seguro?' : 'Are you sure?');
  return (
    // 100001 = above the app's highest overlays (custom modals & popovers top
    // out at 100000). A blocking "are you sure?" must always sit on top of
    // whatever screen invoked it, never behind it.
    <Modal title={title} onClose={() => done(conTexto ? null : false)} zIndex={100001}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
        <div style={{ display: 'flex', gap: '.7rem', alignItems: 'flex-start', fontSize: '.9rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          <AlertTriangle size={18} style={{ color: 'var(--c-amber,#ffb700)', flexShrink: 0, marginTop: 2 }} />
          <span>{o.message}</span>
        </div>
        {conTexto && (
          <textarea
            autoFocus rows={3} value={_confirmTexto} placeholder={o.placeholder || ''}
            onChange={(e) => { _confirmTexto = e.target.value; force(); }}
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem', color: 'var(--c-text,#c8dff0)', fontSize: '.85rem', fontFamily: 'inherit', resize: 'vertical' }}
          />
        )}
        {faltaTexto && (
          <div style={{ fontSize: '.75rem', color: 'var(--c-amber,#ffb700)', marginTop: '-.6rem' }}>
            {o.avisoObligatorio || (es ? 'Escribe el motivo para poder seguir.' : 'Write the reason to continue.')}
          </div>
        )}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button" onClick={() => done(conTexto ? null : false)}
            style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem 1rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}
          >{cancelLabel}</button>
          <button
            type="button" autoFocus={!conTexto} disabled={faltaTexto} onClick={() => done(conTexto ? textoListo : true)}
            style={{ opacity: faltaTexto ? 0.5 : 1, background: danger ? 'var(--c-red,#ff3d6b)' : 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', color: danger ? '#fff' : '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer' }}
          >{confirmLabel}</button>
        </div>
      </div>
    </Modal>
  );
}

export function Input({ label, error, ...p }) {
  // Date inputs use the custom DateField (dark themed, easy month/year nav)
  // instead of the browser's native date picker.
  const isDate = p.type === 'date';
  const { type, ...rest } = p;
  return (
    <div>
      {label && <label className="dkt-input-lbl">{label}</label>}
      {isDate
        ? <DateField {...rest} className="dkt-inp" style={error ? { ...(p.style || {}), borderColor: '#ef4444' } : p.style} />
        : <input {...p} className="dkt-inp" style={error ? { ...(p.style || {}), borderColor: '#ef4444' } : p.style} />}
      {error && <div style={{ fontSize: '.62rem', color: '#ef4444', marginTop: 3, fontWeight: 600 }}>{error}</div>}
    </div>
  );
}

export function Select({ label, children, ...p }) {
  return (
    <div>
      {label && <label className="dkt-input-lbl">{label}</label>}
      <select {...p} className="dkt-inp" style={{ cursor: 'pointer' }}>{children}</select>
    </div>
  );
}

export function Textarea({ label, ...p }) {
  return (
    <div>
      {label && <label className="dkt-input-lbl">{label}</label>}
      <textarea {...p} rows={3} className="dkt-inp" style={{ resize: 'none' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rich note formatting — lightweight **bold** and ==highlight== markers that
// store as PLAIN TEXT (fully backward-compatible with existing notes) and
// render as <strong>/<mark>. Used everywhere a note is written or displayed so
// users can emphasize parts of a note (Lissette 2026-07-09: "poder resaltar o
// poner en negrita" en las notas, en todos lados).
// ---------------------------------------------------------------------------

// Parse a stored note string into React nodes honoring **bold** and
// ==highlight==. Unmatched markers render literally (never throws). Line breaks
// are preserved by the wrapper's whiteSpace:pre-wrap.
const NOTE_MARK_STYLE = { background: 'rgba(255,183,0,.32)', color: 'inherit', borderRadius: 3, padding: '0 2px' };
const NOTE_TAGS = { b: 'strong', i: 'em', u: 'u', mark: 'mark', span: 'span', div: 'span' };

// Árbol de nota (ver noteFormat.js) -> nodos de React. Se pinta a partir del
// árbol YA FILTRADO por la lista blanca, nunca con dangerouslySetInnerHTML: una
// nota es texto que escribió alguien, y aunque el editor solo emite marcado
// acotado, lo guardado pudo llegar por otra vía.
//
// Los bloques salen como <span> en display:block en vez de <div> porque una
// nota se pinta dentro de un <span>, y un <div> ahí dentro es marcado inválido.
function noteTreeToNodes(nodes, keyPrefix) {
  return (nodes || []).map((n, i) => {
    const key = `${keyPrefix}-${i}`;
    if (n.t === 'text') return n.v;
    if (n.t === 'br') return <br key={key} />;
    const Tag = NOTE_TAGS[n.tag] || 'span';
    const style = n.tag === 'mark'
      ? NOTE_MARK_STYLE
      : (n.tag === 'div' ? { display: 'block', ...(n.style || {}) } : (n.style || {}));
    return <Tag key={key} style={style}>{noteTreeToNodes(n.kids, key)}</Tag>;
  });
}

export function renderRichNote(text) {
  const src = String(text ?? '');
  if (!src) return src;
  if (isRichNote(src)) return noteTreeToNodes(parseNoteTree(src), 'r');
  const nodes = [];
  const re = /(\*\*([\s\S]+?)\*\*|==([\s\S]+?)==)/g;
  let last = 0, m, k = 0;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) nodes.push(src.slice(last, m.index));
    if (m[2] != null) nodes.push(<strong key={k++}>{m[2]}</strong>);
    else nodes.push(<mark key={k++} style={{ background: 'rgba(255,183,0,.32)', color: 'inherit', borderRadius: 3, padding: '0 2px' }}>{m[3]}</mark>);
    last = re.lastIndex;
  }
  if (last < src.length) nodes.push(src.slice(last));
  return nodes;
}

// Cualquier nota -> texto plano. Para lo que no sabe pintar formato (HTML
// impreso o enviado por correo, CSV, vistas previas): quita las marcas
// **negrita**/==resaltado== y, si la nota está guardada como HTML acotado, sus
// etiquetas. Sin esto último una nota con cursiva se habría exportado al CSV
// con el marcado a la vista.
export function stripNoteMarks(text) {
  return noteToPlainText(text);
}

// Display a stored note with formatting + preserved line breaks. Drop-in for a
// plain `{note.body}` render: <RichNote text={note.body} />.
export function RichNote({ text, style, className }) {
  return (
    <span className={className} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...style }}>
      {renderRichNote(text)}
    </span>
  );
}

// Note editor: a <textarea> with a tiny toolbar (Bold / Highlight) that wraps
// the current selection in the markers above. Drop-in for a plain note
// textarea — pass `value` + `onChange` (receives the NEW STRING). Any other
// props (placeholder, rows, onKeyDown, onBlur, style…) pass through to the
// underlying <textarea>. `compact` renders a smaller toolbar for inline spots.
// Wrap the current selection of a textarea element in `mark` (e.g. '**'),
// call onChange with the new string, and restore the selection around the
// inner text. Shared by NoteEditor and NoteFormatButtons.
function wrapTextareaSelection(el, value, onChange, mark, placeholder) {
  const v = String(value ?? '');
  const s = el && el.selectionStart != null ? el.selectionStart : v.length;
  const e = el && el.selectionEnd != null ? el.selectionEnd : v.length;
  const sel = v.slice(s, e) || placeholder;
  onChange(v.slice(0, s) + mark + sel + mark + v.slice(e));
  const innerStart = s + mark.length;
  const innerEnd = innerStart + sel.length;
  if (el) requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(innerStart, innerEnd); } catch (_) {} });
}

const noteFmtBtn = (compact) => ({
  display: 'inline-flex', alignItems: 'center', gap: '.25rem',
  fontSize: compact ? '.62rem' : '.68rem', fontWeight: 700,
  padding: compact ? '.15rem .4rem' : '.22rem .55rem', borderRadius: 6, cursor: 'pointer',
  fontFamily: 'var(--font-body)', background: 'var(--c-surface2,#112538)',
  border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1,
});

// ── Barra ampliada: la misma del editor de correos ──────────────────────────
// Lissette 2026-08-14: "quiero el mismo editor que tenemos para los mail en las
// notas del cliente". Son los mismos controles y en el mismo orden que el
// compositor de correo de la ficha: cursiva, subrayado, alineación, tipo de
// letra y tamaño. Negrita y resaltado se quedan donde estaban, a la izquierda.
//
// Solo aparece donde se pide (`full`): las notas sueltas que hay repartidas por
// la app siguen con los dos botones de siempre, que es lo que cabe ahí.
const noteIconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 24, height: 24, borderRadius: 6, padding: 0, cursor: 'pointer',
  border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
  color: 'var(--c-text2,#7aa3c0)',
};
const noteSelStyle = {
  background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
  color: 'var(--c-text,#c8dff0)', borderRadius: 6, fontSize: '.66rem', padding: '2px 3px',
  cursor: 'pointer', fontFamily: 'var(--font-body)',
};

/**
 * Los controles extra. `exec` aplica un comando sobre la caja ya enfocada;
 * `saveSel`/`execSaved` existen porque abrir un <select> roba el foco y deshace
 * la selección: se guarda el rango al pulsar y se restaura antes de aplicar.
 */
function NoteToolbarExtras({ exec, saveSel, execSaved, es }) {
  return (
    <>
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('italic')} style={noteIconBtn} title={es ? 'Cursiva' : 'Italic'}><Italic size={12} /></button>
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('underline')} style={noteIconBtn} title={es ? 'Subrayado' : 'Underline'}><Underline size={12} /></button>
      <span style={{ width: 1, height: 16, background: 'var(--c-border,#1a3550)', margin: '0 1px' }} />
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('justifyLeft')} style={noteIconBtn} title={es ? 'Izquierda' : 'Left'}><AlignLeft size={12} /></button>
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('justifyCenter')} style={noteIconBtn} title={es ? 'Centrar' : 'Center'}><AlignCenter size={12} /></button>
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('justifyRight')} style={noteIconBtn} title={es ? 'Derecha' : 'Right'}><AlignRight size={12} /></button>
      {/* Justificado — Lissette 2026-09-02, queja 9. */}
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('justifyFull')} style={noteIconBtn} title={es ? 'Justificar' : 'Justify'}><AlignJustify size={12} /></button>
      <span style={{ width: 1, height: 16, background: 'var(--c-border,#1a3550)', margin: '0 1px' }} />
      {/* Emoticonos, el mismo panel que en el editor de correo (Lissette
          2026-09-03: "en todos lados donde se redacte correo y notas").
          `saveSel` guarda la seleccion antes de que el foco se vaya al panel;
          `execSaved` la restaura antes de insertar. Es la misma pareja que ya
          usan los desplegables de letra y tamaño, por el mismo motivo. */}
      <EmojiPicker es={es} alPulsar={saveSel} estiloBoton={noteIconBtn}
        onElegir={(emoji) => execSaved('insertText', emoji)} />
      <span style={{ width: 1, height: 16, background: 'var(--c-border,#1a3550)', margin: '0 1px' }} />
      <select title={es ? 'Tipo de letra' : 'Font'} defaultValue="" onMouseDown={saveSel}
        onChange={(e) => { const v = e.target.value; if (v) execSaved('fontName', v); e.target.value = ''; }} style={noteSelStyle}>
        <option value="">{es ? 'Letra' : 'Font'}</option>
        <option value="Arial">Arial</option>
        <option value="Georgia">Georgia</option>
        <option value="Times New Roman">Times</option>
        <option value="Courier New">Courier</option>
        <option value="Verdana">Verdana</option>
      </select>
      <select title={es ? 'Tamaño' : 'Size'} defaultValue="" onMouseDown={saveSel}
        onChange={(e) => { const v = e.target.value; if (v) execSaved('fontSize', v); e.target.value = ''; }} style={noteSelStyle}>
        <option value="">{es ? 'Tamaño' : 'Size'}</option>
        <option value="2">{es ? 'Pequeño' : 'Small'}</option>
        <option value="3">{es ? 'Normal' : 'Normal'}</option>
        <option value="5">{es ? 'Grande' : 'Large'}</option>
        <option value="7">{es ? 'Enorme' : 'Huge'}</option>
      </select>
    </>
  );
}

/**
 * Comandos de formato sobre una caja contenteditable. `getBox` devuelve el nodo
 * y `onAfter` avisa de que hay que volver a serializar (execCommand no siempre
 * dispara un `input`).
 */
function useNoteCommands(getBox, onAfter) {
  const savedRange = useRef(null);
  const exec = (cmd, arg) => {
    const el = getBox();
    if (el && el.focus) el.focus();
    // styleWithCSS hace que el navegador emita estilos en lugar de etiquetas
    // sueltas; el serializador entiende las dos formas, pero así el resultado es
    // consistente entre navegadores.
    try { document.execCommand('styleWithCSS', false, true); } catch (_) { /* no-op */ }
    try { document.execCommand(cmd, false, arg); } catch (_) { /* no-op */ }
    if (onAfter) onAfter();
  };
  const saveSel = () => {
    const el = getBox();
    const s = typeof window !== 'undefined' ? window.getSelection() : null;
    if (s && s.rangeCount && el && el.contains && el.contains(s.anchorNode)) savedRange.current = s.getRangeAt(0).cloneRange();
  };
  const execSaved = (cmd, arg) => {
    const el = getBox();
    if (el && el.focus) el.focus();
    const r = savedRange.current;
    if (r) { const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); }
    exec(cmd, arg);
  };
  return { exec, saveSel, execSaved };
}

// Bold / Highlight buttons that operate on an EXISTING textarea (passed as a
// ref) holding `value`. Use this to add formatting to bespoke note inputs that
// already have their own layout (image paste, send button, etc.) without
// replacing them: attach the same ref to the textarea and pass value+onChange.
export function NoteFormatButtons({ taRef, value, onChange, lang, compact, style, full }) {
  const es = lang !== 'en';
  const btn = noteFmtBtn(compact);
  // El ref todavia no apunta a nada en el primer render, asi que se comprueba
  // despues de montar; sin este re-render los controles no llegaban a aparecer.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const isRichBox = () => {
    const el = taRef && taRef.current;
    return !!(el && el.dataset && el.dataset.richNote === '1');
  };
  // Los controles ampliados solo saben trabajar sobre una caja contenteditable;
  // sobre un <textarea> no hay forma de expresar cursiva o alineación, así que
  // ahí no se pintan aunque se pida `full`.
  const cmds = useNoteCommands(() => (taRef && taRef.current), () => {
    const el = taRef && taRef.current;
    if (el) onChange(noteHtmlToMarkers(el));
  });

  // The same buttons drive two kinds of field. If the ref points at a
  // RichNoteBox (contenteditable), format the live selection with execCommand
  // so the user SEES bold — no markers on screen. If it's still a plain
  // <textarea>, fall back to wrapping the selection in markers, which is all a
  // textarea can do.
  const apply = (cmd, arg, mark, placeholder) => {
    const el = taRef && taRef.current;
    if (el && el.dataset && el.dataset.richNote === '1') {
      el.focus();
      try { document.execCommand(cmd, false, arg); } catch (_) { /* no-op */ }
      // The box emits on input; execCommand doesn't always fire one.
      onChange(noteHtmlToMarkers(el));
      return;
    }
    wrapTextareaSelection(el, value, onChange, mark, placeholder);
  };

  return (
    <div style={{ display: 'flex', gap: '.35rem', alignItems: 'center', flexWrap: 'wrap', ...style }}>
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => apply('bold', undefined, '**', es ? 'texto' : 'text')} style={btn} title={es ? 'Negrita' : 'Bold'}>
        <Bold size={compact ? 11 : 12} /> {es ? 'Negrita' : 'Bold'}
      </button>
      <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => apply('hiliteColor', 'rgba(255,183,0,.32)', '==', es ? 'resaltado' : 'highlight')} style={btn} title={es ? 'Resaltar' : 'Highlight'}>
        <Highlighter size={compact ? 11 : 12} /> {es ? 'Resaltar' : 'Highlight'}
      </button>
      {full && mounted && isRichBox() && <NoteToolbarExtras exec={cmds.exec} saveSel={cmds.saveSel} execSaved={cmds.execSaved} es={es} />}
    </div>
  );
}

// ── WYSIWYG note editor ─────────────────────────────────────────────────────
// Lissette 2026-07-21: "la forma de escribir simbolos es muy confusa, quiero
// algo mas sencillo". The editor used to be a plain <textarea>, so writing in
// bold meant living with literal `**texto**` on screen — markup leaking into
// the user's face.
//
// Now it's a contenteditable: you select and press the button, and you SEE the
// bold. The STORAGE FORMAT IS UNCHANGED (still `**bold**` / `==highlight==`),
// so every existing note keeps rendering, every consumer of the string
// (renderRichNote, stripNoteMarks, exports, emails) keeps working, and nothing
// had to be migrated.

/**
 * Just the editable box — no toolbar. Extracted so the bespoke note composers
 * (client notes, call notes) can be WYSIWYG too: they keep their own footer
 * with the attach button and their own image-paste handling, and only needed
 * the text area swapped. Pass `boxRef` and hand the same ref to
 * NoteFormatButtons so its buttons act on this box.
 *
 * `onPasteFiles(files)` gets any IMAGE files in the clipboard; when it fires,
 * the text path is skipped. Everything else pastes as plain text.
 */
/**
 * `usuarios` (opcional) enciende el desplegable del `@` — ver la nota en
 * `NoteEditor`. Sin él, `@María Fernanda` tecleado a mano sigue llegando igual:
 * quien resuelve la mención es el servidor, no esta caja.
 */
export function RichNoteBox({ value, onChange, boxRef, rows, style, className, placeholder, lang, onPasteFiles, usuarios, onKeyDown: onKeyDownExterno, ...p }) {
  const innerRef = useRef(null);
  const ref = boxRef || innerRef;
  const lastEmitted = useRef(null);
  const es = lang !== 'en';
  const [focused, setFocused] = useState(false);
  const men = useMencionesEnEditable({ usuarios, alInsertar: () => emit() });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = String(value ?? '');
    if (incoming === lastEmitted.current) return; // our own edit, echoed back
    el.innerHTML = noteMarkersToHtml(incoming);
    lastEmitted.current = incoming;
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    const next = noteHtmlToMarkers(el);
    lastEmitted.current = next;
    onChange(next);
  };

  const isEmpty = !String(value ?? '').trim();

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-rich-note="1"
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder || (es ? 'Nota' : 'Note')}
        onInput={() => { emit(); men.revisar(); }}
        onKeyUp={men.revisar}
        onClick={men.revisar}
        // Con el menú del `@` abierto, las flechas y el Enter son suyos.
        onKeyDown={(ev) => { if (!men.onKeyDown(ev)) onKeyDownExterno?.(ev); }}
        onBlur={() => { setFocused(false); emit(); men.onBlur(); }}
        onFocus={() => setFocused(true)}
        onPaste={(ev) => {
          const cd = ev.clipboardData || window.clipboardData;
          if (onPasteFiles) {
            const imgs = [];
            for (const it of (cd?.items || [])) {
              if (it.type && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) imgs.push(f); }
            }
            if (imgs.length) { ev.preventDefault(); onPasteFiles(imgs); return; }
          }
          // Plain text otherwise: pasting from Word or a web page drags in
          // fonts, colours and tables the note format cannot represent. The
          // serializer would flatten them anyway, but the editor would flash
          // the mess first.
          ev.preventDefault();
          const text = cd.getData('text/plain');
          try { document.execCommand('insertText', false, text); } catch (_) { /* no-op */ }
          emit();
        }}
        className={className || 'dkt-inp'}
        style={{
          minHeight: `${(rows || 3) * 1.5 + 1}rem`,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowY: 'auto',
          cursor: 'text',
          ...style,
        }}
        {...p}
      />
      {isEmpty && !focused && placeholder && (
        <div style={{
          position: 'absolute', top: '.55rem', left: '.7rem', pointerEvents: 'none',
          color: 'var(--c-text3,#45688a)', fontSize: '.8rem',
        }}>{placeholder}</div>
      )}
      {men.panel}
    </div>
  );
}

/**
 * `usuarios` (opcional) enciende el desplegable del `@` para nombrar a un
 * compañero (Lissette 2026-09-12). Es solo una ayuda para escribirlo bien:
 * quien resuelve la mención y manda el aviso es el servidor, buscando los
 * nombres del equipo dentro del texto ya guardado. Una caja SIN `usuarios`
 * —que son casi todas— sigue admitiendo `@María Fernanda` tecleado a mano, y
 * llega exactamente igual.
 */
export function NoteEditor({ value, onChange, label, rows, style, className, compact, lang, placeholder, full, usuarios, onKeyDown: onKeyDownExterno, ...p }) {
  const ref = useRef(null);
  // What we last handed to onChange. Used to tell "the parent echoed our own
  // edit back" (leave the DOM alone — rewriting it would kill the caret) from
  // "the value genuinely changed elsewhere" (reset the DOM).
  const lastEmitted = useRef(null);
  const es = lang !== 'en';
  const btn = noteFmtBtn(compact);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = String(value ?? '');
    if (incoming === lastEmitted.current) return; // our own edit, echoed back
    el.innerHTML = noteMarkersToHtml(incoming);
    lastEmitted.current = incoming;
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    const next = noteHtmlToMarkers(el);
    lastEmitted.current = next;
    onChange(next);
  };

  const cmds = useNoteCommands(() => ref.current, emit);
  const exec = cmds.exec;
  // El desplegable del `@`. `emit` como `alInsertar` porque el menú cambia el
  // nodo de texto a mano y eso no dispara `onInput`.
  const men = useMencionesEnEditable({ usuarios, alInsertar: emit });
  // La barra ampliada va donde hay sitio: en las notas a pantalla completa sí,
  // en los recuadros `compact` incrustados en una fila no.
  const showFull = full === undefined ? !compact : full;

  const isEmpty = !String(value ?? '').trim();

  return (
    <div>
      {label && <label className="dkt-input-lbl">{label}</label>}
      <div style={{ display: 'flex', gap: '.35rem', marginBottom: '.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('bold')} style={btn} title={es ? 'Negrita' : 'Bold'}>
          <Bold size={compact ? 11 : 12} /> {es ? 'Negrita' : 'Bold'}
        </button>
        <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => exec('hiliteColor', 'rgba(255,183,0,.32)')} style={btn} title={es ? 'Resaltar' : 'Highlight'}>
          <Highlighter size={compact ? 11 : 12} /> {es ? 'Resaltar' : 'Highlight'}
        </button>
        {showFull && <NoteToolbarExtras exec={cmds.exec} saveSel={cmds.saveSel} execSaved={cmds.execSaved} es={es} />}
      </div>
      <div style={{ position: 'relative' }}>
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label || placeholder || (es ? 'Nota' : 'Note')}
          onInput={() => { emit(); men.revisar(); }}
          onKeyUp={men.revisar}
          onClick={men.revisar}
          // El menú se queda con las flechas y el Enter mientras está abierto;
          // si no, el Enter enviaría la nota a medio nombre. Devuelve `false`
          // cuando no lo usa, y entonces la tecla sigue su camino normal.
          onKeyDown={(ev) => { if (!men.onKeyDown(ev)) onKeyDownExterno?.(ev); }}
          onBlur={() => { setFocused(false); emit(); men.onBlur(); }}
          onFocus={() => setFocused(true)}
          // Paste as PLAIN TEXT. Pasting from Word or a web page otherwise
          // drags in fonts, colors and tables that the note format cannot
          // represent — the serializer would flatten them anyway, but the
          // editor would flash the mess first.
          onPaste={(ev) => {
            ev.preventDefault();
            const text = (ev.clipboardData || window.clipboardData).getData('text/plain');
            try { document.execCommand('insertText', false, text); } catch (_) { /* no-op */ }
            emit();
          }}
          className={className || 'dkt-inp'}
          style={{
            minHeight: `${(rows || 3) * 1.5 + 1}rem`,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowY: 'auto',
            cursor: 'text',
            ...style,
          }}
          {...p}
        />
        {isEmpty && !focused && placeholder && (
          <div style={{
            position: 'absolute', top: '.55rem', left: '.7rem', pointerEvents: 'none',
            color: 'var(--c-text3,#45688a)', fontSize: '.8rem',
          }}>{placeholder}</div>
        )}
        {men.panel}
      </div>
    </div>
  );
}

// Combobox — text input + dropdown that reveals the FULL option list as soon as
// the field is focused/clicked, WITHOUT making the user delete the value first.
// Native <datalist> filters its options by the current input text, so a field
// that already held a value showed an empty menu until you cleared it — every
// agent kept erasing the name to see the list (Lissette 2026-06-17: "todos los
// toggles… que salga la lista sin tener que borrar el nombre"). Free text is
// still allowed: typing narrows the list but the typed value is kept. The menu
// renders in a body portal (fixed-positioned to the input) so it's never
// clipped by a scrollable modal body.
//
// `onPick` / `onBlur` / `disabled` / `maxLength` son OPCIONALES y se anadieron
// para las casillas de las columnas propias (Lissette 2026-09-10), que guardan al
// salir y no en cada tecla. Elegir una opcion NO dispara blur (el menu usa
// onMouseDown + preventDefault para no perder el foco), asi que sin `onPick` una
// eleccion con raton se quedaria sin guardar hasta que la persona picara fuera.
export function Combobox({ value, onChange, options = [], label, placeholder, className, inputStyle, style, lang, name, required, onPick, onBlur, disabled, maxLength, title }) {
  const [open, setOpen] = useState(false);
  // null  → not editing: mirror `value` and show ALL options.
  // string → user is typing: show + filter by this text.
  const [query, setQuery] = useState(null);
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const text = query == null ? (value || '') : query;
  const opts = Array.isArray(options) ? [...new Set(options.filter(Boolean).map(String))] : [];
  const filtered = (query == null || query === '')
    ? opts
    : opts.filter((o) => o.toLowerCase().includes(query.toLowerCase()));

  const place = () => { const el = inputRef.current; if (el) setRect(el.getBoundingClientRect()); };
  useEffect(() => {
    if (!open) return;
    place();
    const reposition = () => place();
    const onDown = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inPop = e.target.closest && e.target.closest('[data-dkt-combo-pop]');
      if (!inWrap && !inPop) { setOpen(false); setQuery(null); }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    // Capture phase (true): the menu is often inside a <Modal>, whose .dkt-modal
    // stops mousedown propagation — a bubble-phase document listener would never
    // fire, so clicking elsewhere in the form left the menu open (Lissette
    // 2026-06-18). Capturing on document sees the click before any stopPropagation.
    document.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open]);

  const pick = (v) => { onChange && onChange(v); setQuery(null); setOpen(false); onPick && onPick(v); };

  const pop = open && rect && filtered.length > 0
    ? createPortal(
        <div data-dkt-combo-pop style={{ position: 'fixed', zIndex: 100000, left: rect.left, top: rect.bottom + 2, width: rect.width, maxHeight: 240, overflowY: 'auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,.5)' }}>
          {filtered.map((o) => (
            <div key={o} onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              style={{ padding: '.4rem .6rem', fontSize: '.8rem', cursor: 'pointer', color: 'var(--c-text,#c8dff0)', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--c-surface3,#162d44)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o}</span>
              {o === value && <span style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }}>✓</span>}
            </div>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...(style || {}) }}>
      {label && <label className="dkt-input-lbl">{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          className={className !== undefined ? className : 'dkt-inp'}
          value={text}
          name={name}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          title={title}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onClick={() => { setOpen(true); place(); }}
          onChange={(e) => { setQuery(e.target.value); onChange && onChange(e.target.value); setOpen(true); }}
          onBlur={(e) => { setQuery(null); onBlur && onBlur(e); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQuery(null); } else if (e.key === 'Enter') { setOpen(false); } }}
          style={{ width: '100%', boxSizing: 'border-box', paddingRight: 24, ...(inputStyle || {}) }}
        />
        <span onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); place(); }} aria-hidden="true"
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--c-text3,#45688a)', fontSize: 11, lineHeight: 1 }}>▾</span>
      </div>
      {pop}
    </div>
  );
}

// `icon` is optional. It renders as <Icon/>, so a caller that omits it used to
// mount `undefined` as a component and take the whole screen down with React's
// "Element type is invalid … got: undefined" — caught by the ErrorBoundary, so
// the user just saw "Algo salió mal en esta pantalla" with no clue why. That is
// exactly what the Platform Admin BAA tab hit (2026-07-28 → 2026-08-10, 13
// crashes across 6 users): its four summary cards pass only label + value.
// Guarded here rather than at the call sites so no future caller can reopen it.
export function StatCard({ icon: Icon, label, value, accent, sub, onClick }) {
  const colorMap = {
    'bg-blue-500':    { c: '--c-cyan',  bg: '--c-cyan-dim'  },
    'bg-emerald-500': { c: '--c-green', bg: '--c-green-dim' },
    'bg-amber-500':   { c: '--c-cyan',  bg: '--c-cyan-dim'  },
    'bg-purple-500':  { c: '--c-green', bg: '--c-green-dim' },
    'bg-red-500':     { c: '--c-red',   bg: '--c-red-dim'   },
    'bg-gray-500':    { c: '--sc-gray', bg: '--sc-gray-bg'  },
  };
  const m = colorMap[accent] || colorMap['bg-blue-500'];
  return (
    <div onClick={onClick} className={`dkt-stat${onClick ? ' dkt-stat-click' : ''} dkt-sc`} data-sc={accent} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="dkt-stat-glow" />
      {Icon && <div className="dkt-stat-icon" style={{ background: `var(${m.bg})` }}><Icon size={18} style={{ color: `var(${m.c})` }} /></div>}
      <div className="dkt-stat-val" style={{ color: `var(${m.c})` }}>{value}</div>
      <div className="dkt-stat-lbl">{label}</div>
      {sub && <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function InfoBlock({ label, value, mono }) {
  return (
    <div style={{ marginBottom: '.4rem' }}>
      <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontFamily: 'var(--font-mono)', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)' }}>{value || '--'}</div>
    </div>
  );
}

export function Avatar({ client, size = 8 }) {
  const px = size * 4;
  const hue = ((client.firstName.charCodeAt(0) || 65) + (client.lastName.charCodeAt(0) || 65)) % 360;
  const bg = `hsl(${hue},70%,35%)`;
  return (
    <div className="dkt-av" style={{ width: px, height: px, background: bg, fontSize: px * 0.32 }}>
      {client.firstName[0]}{client.lastName[0]}
    </div>
  );
}
